/**
 * Field labels and EPA Form 8700-22 box numbers for manifest discrepancy
 * fields. This is the union of two maps that used to drift independently:
 * `encamp-waste-data-access`'s `chFormat.ts` and Oak's
 * `DiscrepancyReviewDialog.helpers.tsx`. Both packages now import from here.
 * Every screen that names a discrepancy field reads it from this map.
 */

export const FIELD_LABELS: Record<string, string> = {
  generatorName: "Generator Name",
  generatorId: "Generator EPA ID",
  generatorStreetAddress: "Generator Street Address",
  generatorStreet2: "Generator Street 2",
  generatorCity: "Generator City",
  generatorState: "Generator State",
  generatorZip: "Generator ZIP",
  shippedDate: "Ship Date",
  destinationFacilityId: "TSDF EPA ID",
  destinationFacilityName: "TSDF Name",
  wasteLinesCount: "Waste Line Count",
  quantity: "Quantity",
  quantityUnitOfMeasureCode: "UOM Code",
  quantityUnitOfMeasureDescription: "UOM Description",
  containerTypeCode: "Container Type",
  formCode: "Form Code",
  sourceCode: "Source Code",
  managementMethodCode: "Management Method Code",
  federalWasteCodes: "Federal Waste Codes",
  stateWasteCodes: "State Waste Codes",
  dotPrintedInformation: "DOT Description",
  dotProperShippingName: "DOT Proper Shipping Name",
  nonHazWasteDescription: "Non-Haz Description",
  unpairedWasteLine: "Missing Waste Line",
  transporter1Name: "Transporter 1 Name",
  transporter1Id: "Transporter 1 EPA ID",
  transporter2Name: "Transporter 2 Name",
  transporter2Id: "Transporter 2 EPA ID",
  transporter3Id: "Transporter 3 EPA ID",
  generatorCertification: "Generator Certification",
  transporterCertification: "Transporter Certification",
  tsdfCertification: "TSDF Certification",
  eManifestMissing: "Missing e-Manifest",
  epaCorrectionRequest: "EPA-Requested Correction",
  wasteCodeDotConsistency: "Waste Code / DOT Consistency",
  brDensity: "Density",
};

/** Fields staff can report a correction on from a generator status finding: the ones that can change a calculated status. */
export const GENERATOR_STATUS_CORRECTION_FIELDS = [
  "quantity",
  "quantityUnitOfMeasureCode",
  "federalWasteCodes",
  "stateWasteCodes",
  "nonHazWasteDescription",
  "generatorId",
] as const;

/** Of those, the ones that belong to the whole manifest rather than one waste line. */
const MANIFEST_LEVEL_CORRECTION_FIELDS = new Set<string>(["generatorId"]);

export function isManifestLevelCorrectionField(field: string): boolean {
  return MANIFEST_LEVEL_CORRECTION_FIELDS.has(field);
}

/** Human-readable label for a discrepancy `field`, falling back to the raw field name. */
export function getFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/**
 * Keyed by discrepancy `field`. Matches `compareManifestVsVendorFields.ts`,
 * `compareManifestVsDocumentFields.ts`, and COMPLIANCE_FIELDS. If you add
 * detection for a new field, add its box here and the CH export/dialog will
 * pick it up automatically.
 *
 * Fields with no numeric section on EPA Form 8700-22 are simply absent from
 * this map — callers fall back to a placeholder. `eManifestMissing` is
 * compliance-only. `brDensity` (Biennial Report density) travels with the
 * eManifest rather than as a numbered box on the form.
 */
export const FIELD_EPA_BOX: Record<string, string> = {
  generatorName: "1",
  generatorId: "1",
  generatorStreetAddress: "1",
  generatorStreet2: "1",
  generatorCity: "1",
  generatorState: "1",
  generatorZip: "1",
  destinationFacilityId: "8",
  wasteLinesCount: "9b",
  unpairedWasteLine: "9b", // structural waste-line discrepancy; same section as wasteLinesCount
  dotPrintedInformation: "9b", // U.S. DOT Description on EPA Form 8700-22
  nonHazWasteDescription: "9b", // Non-haz description shares the 9b waste-line column
  containerTypeCode: "10",
  quantity: "11",
  federalWasteCodes: "13",
  stateWasteCodes: "13",
  shippedDate: "15",
  managementMethodCode: "19",
};

/** EPA Form 8700-22 box number for a discrepancy `field`, or null if it has none. */
export function getEpaFormBox(field: string): string | null {
  return FIELD_EPA_BOX[field] ?? null;
}

/**
 * Fields whose undo cannot represent a null prior value. The waste-code restore
 * writers map null to `[]`, so verification fails after a technically correct
 * write.
 *
 * Shared because both sides of the undo have to agree: the server refuses these
 * in `assertUndoSafe`, and the dialog greys the button out for the same rows
 * rather than letting a reviewer click into that refusal.
 */
export const NULL_PRIOR_UNRESTORABLE_FIELDS: ReadonlySet<string> = new Set([
  "federalWasteCodes",
  "stateWasteCodes",
]);
